// Lib
import { FC, memo, useState } from "react";
// Hooks
import { useViewport } from "hooks";
// Types
import { CompleteProductData, MenuStatuses } from "../../types";
// Helpers
import { renderSubtotal } from "../../helpers";
// Icons
import { DiningIcon, PencilIcon, PlusIcon, TrashIcon } from "icons";
// Components
import { Switch } from "components/Form";
import { ConfirmDialog } from "components/Modals";
import { EmptyState } from "../EmptyState";
import { BlockedState } from "../BlockedState";
import { AddItem } from "../AddItem";
// Styled
import { ContentBox, FlexContainer, HorizontalDevider } from "styled/Box";
import { Typography } from "styled/Typography";
import { Button } from "styled/Buttons";
import { Addon, QuantityContainer } from "./styled";

interface OrderDetailsProps {
  isBlocked: boolean;
  menuStatuses: MenuStatuses;
  locationId: string;
  orderItems: CompleteProductData[];
  isCutlery: boolean;
  setMenuId: (id: string) => void;
  setMenuStatuses: (data: MenuStatuses) => void;
  handleAddOrderItem: (data: CompleteProductData) => void;
  handleRemoveOrderItem: (id: string) => void;
  setIsCutlery: (data: boolean) => void;
}

const OrderDetailsComponent: FC<OrderDetailsProps> = ({
  isBlocked,
  menuStatuses,
  locationId,
  orderItems,
  isCutlery,
  setMenuId,
  setMenuStatuses,
  handleAddOrderItem,
  handleRemoveOrderItem,
  setIsCutlery,
}) => {
  const { isMinTablet } = useViewport();

  const [itemModal, setItemModal] = useState<boolean | CompleteProductData>(
    false,
  );

  const [removeItemModal, setRemoveItemModal] = useState<
    CompleteProductData | false
  >(false);

  const handleOpenModal = () => {
    setItemModal(true);
  };

  const handleCloseModal = () => {
    setItemModal(false);
  };

  const onRemoveItem = () => {
    if (!removeItemModal) {
      return;
    }
    handleRemoveOrderItem(removeItemModal.uniqId);
    setRemoveItemModal(false);
  };

  const onAddItem = (data: CompleteProductData) => {
    setItemModal(false);
    handleAddOrderItem(data);
  };

  const { isClosed } = menuStatuses;

  const isUnAvailable = isBlocked || isClosed;

  return (
    <>
      <ContentBox $column $gap={16} $position="relative">
        <FlexContainer $fullwidth $align="center" $justify="space-between">
          <Typography.H2>Order Details</Typography.H2>

          <Button.Form2 icon={<PlusIcon />} onClick={handleOpenModal}>
            Add item
          </Button.Form2>
        </FlexContainer>

        {!orderItems?.length ? (
          <EmptyState
            Icon={<DiningIcon />}
            title="Selected item will show here"
          />
        ) : (
          orderItems.map(
            ({
              product,
              addonsModifiers,
              quantity,
              uniqId,
              remark,
              combos,
            }) => (
              <FlexContainer
                key={uniqId}
                $align={isMinTablet ? "flex-start" : "center"}
                $fullwidth
                $justify="space-between"
                $column={isMinTablet}
              >
                <FlexContainer $gap={12} $align="center">
                  <QuantityContainer>
                    <Typography.Title>{quantity}</Typography.Title>
                  </QuantityContainer>

                  <div>
                    <Typography.Title>{product.name}</Typography.Title>

                    {!!remark && <Addon>{remark}</Addon>}

                    {!!addonsModifiers?.addon?.length &&
                      addonsModifiers?.addon.map(a => (
                        <Addon key={a.id}>
                          {a.quantity} x {a.name}
                        </Addon>
                      ))}

                    {!!addonsModifiers?.modifier?.length &&
                      addonsModifiers?.modifier.map(m => (
                        <Addon key={m.id}>{m.name}</Addon>
                      ))}
                  </div>
                </FlexContainer>

                <FlexContainer
                  $align="center"
                  $justify={isMinTablet ? "space-between" : undefined}
                  $fullwidth={isMinTablet}
                >
                  {renderSubtotal({
                    product,
                    addonsModifiers,
                    quantity,
                    remark,
                    combos,
                  })}

                  <FlexContainer $gap={8} $margin="0 0 0 24px">
                    <Button.Form
                      icon={<PencilIcon />}
                      style={{ width: 40, minWidth: 40 }}
                      onClick={() =>
                        setItemModal({
                          product,
                          addonsModifiers,
                          quantity,
                          uniqId,
                          remark,
                          combos,
                        })
                      }
                    />

                    <Button.Form
                      icon={<TrashIcon fill="#646965" />}
                      style={{ width: 40, minWidth: 40 }}
                      onClick={() =>
                        setRemoveItemModal({
                          product,
                          addonsModifiers,
                          quantity,
                          uniqId,
                          remark,
                          combos,
                        })
                      }
                    />
                  </FlexContainer>
                </FlexContainer>
              </FlexContainer>
            ),
          )
        )}

        <HorizontalDevider />

        <FlexContainer $fullwidth $align="center" $justify="space-between">
          <Typography.Title>Use cutlery</Typography.Title>

          <Switch checked={isCutlery} onChange={value => setIsCutlery(value)} />
        </FlexContainer>

        {isUnAvailable && <BlockedState isClosed={isClosed} />}
      </ContentBox>

      <AddItem
        locationId={locationId}
        itemData={itemModal}
        setMenuId={setMenuId}
        setMenuStatuses={setMenuStatuses}
        onAddItem={onAddItem}
        onClose={handleCloseModal}
      />

      <ConfirmDialog
        isLoading={false}
        open={!!removeItemModal}
        Icon={TrashIcon}
        message={
          !!removeItemModal && `${removeItemModal.product.name} will be removed`
        }
        description="Are you sure to continue this action?"
        onCancel={() => setRemoveItemModal(false)}
        firstCTAButton={{
          title: "Remove Product",
          status: "danger",
          onClick: onRemoveItem,
        }}
      />
    </>
  );
};

export const OrderDetails = memo(OrderDetailsComponent);
